import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import api, { errMsg, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Slider } from "@/components/ui/slider";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useSharedLocation } from "@/context/LocationContext";
import { LocationDeniedNote } from "@/components/LocationFilterBar";
import { MapPin, Users, Plus, Trash2, X, CheckCircle2, MessageCircle, Navigation, Send, CalendarDays, ChevronLeft, ChevronRight, List, LayoutGrid, Upload, Search, ArrowLeft } from "lucide-react";
import { AddToCalendarButton } from "@/components/AddToCalendarButton";
import SafetyRulesContent from "@/components/SafetyRulesContent";

const EMPTY_MEETUP = { title: "", description: "", date: "", start_time: "", location: "", age_range: "", capacity: 15, kind: "meetup", category: "Meet-up", image_url: "" };
const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// Module-scoped so its identity is stable across Meetups re-renders. Owning
// the chat text locally means typing doesn't re-render the parent Meetups
// tree, which prevents the inline MeetupCard from being unmounted on every
// keystroke — that unmount was stealing input focus and letting the space
// key bubble to document.body (which then scrolled the page).
function ChatInputBox({ eventId, onSend }) {
  const [text, setText] = useState("");
  const inputRef = useRef(null);
  const submit = async () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    const ok = await onSend(trimmed);
    if (ok) setText("");
    // keep focus regardless so the user can keep typing without re-clicking
    setTimeout(() => inputRef.current?.focus(), 0);
  };
  return (
    <div className="flex gap-2">
      <Input
        ref={inputRef}
        autoFocus
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          // Prevent the space key from bubbling to the document (which would
          // otherwise scroll the page when the input momentarily loses focus).
          if (e.key === " ") e.stopPropagation();
          if (e.key === "Enter") { e.preventDefault(); submit(); }
        }}
        placeholder="Ask a question or say hi..."
        className="rounded-full bg-white border-sage/30"
        data-testid={`chat-input-${eventId}`}
      />
      <Button size="sm" onClick={submit} disabled={!text.trim()} className="rounded-full bg-sage hover:bg-sage-dark text-white shrink-0" data-testid={`chat-send-${eventId}`}>
        <Send className="w-4 h-4" />
      </Button>
    </div>
  );
}

function MeetupCard({ m, user, isParent, chatFor, setChatFor, messages, chatEndRef, cancelRsvp, setRsvpFor, setRsvp, deleteMeetup, sendChat, detailMode = false, onOpenDetail }) {
  const full = m.capacity != null && m.spots_filled >= m.capacity;
  const mine = !!m.my_rsvp;
  const isHost = user && (user.user_id === m.created_by || user.role === "admin");
  const [attendeesOpen, setAttendeesOpen] = useState(detailMode);
  return (
    <div className="bg-white/80 border border-sage/20 rounded-3xl overflow-hidden shadow-sm hover:shadow-md transition-shadow" data-testid={`meetup-card-${m.event_id}`}>
      <div className="sm:flex">
        <div className="sm:w-36 shrink-0 bg-terracotta-light overflow-hidden flex flex-col">
          <div className="px-3 pt-3 text-left" data-testid={`meetup-date-${m.event_id}`}>
            <span className="block text-3xl font-black font-heading text-terracotta-dark leading-none">{m.date.slice(8, 10)}</span>
            <span className="block text-xs font-bold uppercase tracking-wide text-forest/60 mt-0.5">
              {new Date(m.date + "T12:00:00").toLocaleDateString(undefined, { month: "short", year: "numeric" })}
            </span>
          </div>
          <img
            src={m.image_url ? (/^https?:\/\//i.test(m.image_url) ? m.image_url : fileUrl(m.image_url)) : "/logo.png"}
            alt={m.image_url ? `${m.title} photo` : "Compass Kids logo"}
            onError={(e) => { if (!e.currentTarget.dataset.fallback) { e.currentTarget.dataset.fallback = "1"; e.currentTarget.src = "/logo.png"; } }}
            className="w-full h-32 sm:h-full sm:flex-1 object-contain mt-3 bg-sage-light"
            data-testid={`meetup-image-${m.event_id}`}
          />
        </div>
        <div className="p-5 sm:p-6 flex-1 min-w-0">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="flex flex-wrap items-center gap-1.5">
                {m.age_range && (
                  <span className="text-[10px] font-bold uppercase tracking-wide bg-amber-100 text-amber-800 px-2.5 py-1 rounded-full" data-testid={`age-badge-${m.event_id}`}>{m.age_range}</span>
                )}
                {m.distance_km != null && (
                  <span className="text-[10px] font-bold uppercase tracking-wide bg-sage text-white px-2.5 py-1 rounded-full" data-testid={`distance-${m.event_id}`}>
                    {m.distance_km} km away
                  </span>
                )}
              </div>
              <h3 className="font-heading font-bold text-lg text-forest mt-2">
                {detailMode ? (
                  <span data-testid={`meetup-link-${m.event_id}`}>{m.title}</span>
                ) : (
                  <button
                    type="button"
                    onClick={() => onOpenDetail && onOpenDetail(m.event_id)}
                    className="text-left hover:text-terracotta-dark transition-colors cursor-pointer bg-transparent p-0 border-0"
                    data-testid={`meetup-link-${m.event_id}`}
                  >
                    {m.title}
                  </button>
                )}
              </h3>
              <div className="flex flex-wrap items-center gap-4 mt-1 text-xs text-forest/60">
                {m.start_time && <span className="flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" />{m.start_time}</span>}
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(m.location)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-sage-dark hover:text-terracotta-dark hover:underline transition-colors"
                  data-testid={`meetup-location-link-${m.event_id}`}
                  aria-label={`Open ${m.location} in Google Maps`}
                >
                  <MapPin className="w-3.5 h-3.5" />{m.location}
                </a>
                <span className="text-forest/40">hosted by {m.creator_name}</span>
              </div>
            </div>
            {isParent && (
              mine ? (
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold bg-sage-light text-sage-dark px-3 py-1.5 rounded-full flex items-center gap-1" data-testid={`rsvpd-badge-${m.event_id}`}>
                    <CheckCircle2 className="w-3.5 h-3.5" /> Going
                  </span>
                  <Button size="sm" variant="ghost" className="rounded-full text-terracotta-dark hover:bg-terracotta-light" onClick={() => cancelRsvp(m.event_id)} data-testid={`cancel-rsvp-${m.event_id}`}>
                    Cancel
                  </Button>
                </div>
              ) : (
                <Button size="sm" disabled={full} onClick={() => { setRsvpFor(m); setRsvp({ children_count: 0, adults_count: 0, children: [], note: "" }); }}
                  className="rounded-full bg-terracotta hover:bg-terracotta-dark text-white" data-testid={`join-btn-${m.event_id}`}>
                  {full ? "Full" : "Join meet-up"}
                </Button>
              )
            )}
            {!user && <span className="text-xs text-forest/50">Sign in to join</span>}
          </div>
          {m.description && <p className="text-sm text-forest/70 mt-3 leading-relaxed">{m.description}</p>}
          <div className="mt-4 max-w-xs">
            <div className="flex justify-between text-xs font-semibold text-forest/60 mb-1">
              <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> Spots</span>
              <span data-testid={`spots-${m.event_id}`}>{m.capacity == null ? `${m.spots_filled} joined · No limit` : `${m.spots_filled}/${m.capacity} filled`}</span>
            </div>
            {m.capacity != null && <Progress value={Math.min(100, (m.spots_filled / m.capacity) * 100)} className="h-2 bg-sage-light" />}
          </div>
          <div className="flex flex-wrap gap-2 mt-4">
            {user && (
              <Button size="sm" variant="outline" className="rounded-full border-sage/40" onClick={() => setChatFor(chatFor === m.event_id ? null : m.event_id)} data-testid={`chat-toggle-${m.event_id}`}>
                <MessageCircle className="w-4 h-4 mr-1" />
                {chatFor === m.event_id ? "Hide chat" : "Questions & chat"}
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              className="rounded-full border-sage/40"
              onClick={() => setAttendeesOpen((o) => !o)}
              aria-expanded={attendeesOpen}
              data-testid={`attendees-toggle-${m.event_id}`}
            >
              <Users className="w-4 h-4 mr-1" />
              {attendeesOpen ? "Hide attendees" : `Show attendees (${m.attendee_count || 0})`}
            </Button>
            {isHost && (
              <Button size="sm" variant="ghost" className="rounded-full text-terracotta-dark hover:bg-terracotta-light" onClick={() => deleteMeetup(m.event_id)} data-testid={`delete-meetup-${m.event_id}`}>
                <Trash2 className="w-4 h-4 mr-1" /> Remove
              </Button>
            )}
            <AddToCalendarButton event={m} testIdSuffix={m.event_id} />
          </div>
          {attendeesOpen && (
            <div className="mt-3 bg-softstone/60 rounded-2xl p-4 space-y-2" data-testid={`attendees-list-${m.event_id}`}>
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-sage-dark" />
                <h4 className="text-sm font-bold text-forest" data-testid={`attendees-heading-${m.event_id}`}>
                  Attendees ({m.attendee_count || 0})
                </h4>
              </div>
              {(!m.attendees || m.attendees.length === 0) && (
                <p className="text-xs text-forest/50" data-testid={`no-attendees-${m.event_id}`}>No one has joined yet.</p>
              )}
              {(m.attendees || []).map((a) => {
                const parts = (a.parent_name || "").trim().split(/\s+/);
                const surname = parts.length > 1 ? parts[parts.length - 1] : (parts[0] || "Family");
                const familyLabel = `The ${surname}${surname.endsWith("s") ? "" : "s"}`;
                // Prefer the children explicitly saved on this RSVP (with
                // gender+age from the Join dialog). Fall back to the parent's
                // profile children only if the RSVP didn't include per-child data.
                const kids = (a.children && a.children.length > 0)
                  ? a.children.map((c) => ({ age: c.age, gender: c.gender || "" }))
                  : (a.family_children || []);
                const childCount = a.children_count || 0;
                const adultsCount = a.adults_count || 0;
                return (
                  <div key={a.rsvp_id} data-testid={`attendee-row-${a.rsvp_id}`}>
                    <div className="text-sm text-forest/80 flex flex-wrap gap-x-3">
                      <span className="font-bold" data-testid={`attendee-family-${a.rsvp_id}`}>{familyLabel}</span>
                      <span data-testid={`attendee-parents-${a.rsvp_id}`}>
                        {adultsCount} parent{adultsCount === 1 ? "" : "s"}
                      </span>
                      <span data-testid={`attendee-headcount-${a.rsvp_id}`}>
                        {childCount} child{childCount === 1 ? "" : "ren"}
                      </span>
                      {kids.length > 0 && (
                        <span className="text-forest/60" data-testid={`attendee-kids-${a.rsvp_id}`}>
                          ({kids.map((k, i) => {
                            const bits = [];
                            if (k.age !== undefined && k.age !== "") bits.push(`age ${k.age}`);
                            if (k.gender) bits.push(k.gender);
                            return bits.join(" · ") || `child ${i + 1}`;
                          }).join(", ")})
                        </span>
                      )}
                    </div>
                    {isHost && a.note && (
                      <p className="text-xs text-forest/60 italic mt-1 whitespace-pre-wrap break-words" data-testid={`attendee-note-${a.rsvp_id}`}>
                        “{a.note}”
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {chatFor === m.event_id && (
            <div className="mt-3 bg-softstone/60 rounded-2xl p-4 overflow-hidden min-w-0" data-testid={`chat-panel-${m.event_id}`}>
              <div className="max-h-56 overflow-y-auto space-y-3 mb-3 pr-1">
                {messages.length === 0 && (
                  <p className="text-xs text-forest/50 py-2">No messages yet — ask the host a question!</p>
                )}
                {messages.map((msg) => {
                  const own = user && msg.user_id === user.user_id;
                  return (
                    <div key={msg.message_id} className={`flex ${own ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 ${own ? "bg-sage text-white" : "bg-white border border-sage/20"}`}>
                        <p className={`text-[10px] font-bold mb-0.5 ${own ? "text-white/70" : "text-terracotta-dark"}`}>
                          {msg.name}{msg.user_id === m.created_by ? " · host" : ""}
                        </p>
                        <p className="text-sm leading-snug whitespace-pre-wrap break-words">{msg.text}</p>
                        <p className={`text-[9px] mt-1 ${own ? "text-white/60" : "text-forest/40"}`}>
                          {new Date(msg.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                        </p>
                      </div>
                    </div>
                  );
                })}
                <div ref={chatEndRef} />
              </div>
              <ChatInputBox eventId={m.event_id} onSend={(text) => sendChat(m.event_id, text)} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


function CalendarGridView({ meetups, calMonth, setCalMonth, selectedDay, setSelectedDay }) {
  const { year, month } = calMonth;
  const firstDay = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startWeekday = firstDay.getDay();
  const todayStr = new Date().toISOString().slice(0, 10);

  const byDay = {};
  for (const m of meetups) {
    const d = m.date;
    if (!d) continue;
    (byDay[d] = byDay[d] || []).push(m);
  }

  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    cells.push({ day: d, dateStr, events: byDay[dateStr] || [] });
  }
  while (cells.length % 7 !== 0) cells.push(null);

  const prevMonth = () => {
    setSelectedDay(null);
    setCalMonth((c) => (c.month === 0 ? { year: c.year - 1, month: 11 } : { year: c.year, month: c.month - 1 }));
  };
  const nextMonth = () => {
    setSelectedDay(null);
    setCalMonth((c) => (c.month === 11 ? { year: c.year + 1, month: 0 } : { year: c.year, month: c.month + 1 }));
  };
  const goToday = () => {
    const n = new Date();
    setCalMonth({ year: n.getFullYear(), month: n.getMonth() });
    setSelectedDay(n.toISOString().slice(0, 10));
  };

  const selectedEvents = selectedDay ? (byDay[selectedDay] || []) : [];

  return (
    <div data-testid="meetups-calendar-view">
      <div className="bg-white/80 border border-sage/20 rounded-3xl p-4 sm:p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <Button size="sm" variant="outline" className="rounded-full border-sage/40" onClick={prevMonth} data-testid="cal-prev-month-btn" aria-label="Previous month">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="flex items-center gap-3">
            <h3 className="text-lg sm:text-xl font-black text-forest font-heading" data-testid="cal-month-label">
              {MONTH_NAMES[month]} {year}
            </h3>
            <Button size="sm" variant="ghost" className="rounded-full text-xs text-sage-dark hover:bg-sage-light" onClick={goToday} data-testid="cal-today-btn">
              Today
            </Button>
          </div>
          <Button size="sm" variant="outline" className="rounded-full border-sage/40" onClick={nextMonth} data-testid="cal-next-month-btn" aria-label="Next month">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
        <div className="grid grid-cols-7 gap-1 mb-1">
          {WEEKDAYS.map((w) => (
            <div key={w} className="text-center text-[10px] sm:text-xs font-bold uppercase tracking-wide text-forest/50 py-1">
              {w}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((cell, idx) => {
            if (!cell) return <div key={`e-${idx}`} className="aspect-square" />;
            const isToday = cell.dateStr === todayStr;
            const isSelected = cell.dateStr === selectedDay;
            const hasEvents = cell.events.length > 0;
            return (
              <button
                key={cell.dateStr}
                type="button"
                onClick={() => hasEvents ? setSelectedDay(isSelected ? null : cell.dateStr) : setSelectedDay(cell.dateStr)}
                className={[
                  "aspect-square rounded-xl p-1 sm:p-1.5 flex flex-col items-stretch text-left transition-colors border",
                  isSelected ? "bg-sage text-white border-sage-dark" : isToday ? "bg-terracotta-light border-terracotta/40" : "bg-softstone/40 border-transparent hover:bg-sage-light/60",
                  hasEvents ? "cursor-pointer" : "cursor-default",
                ].join(" ")}
                data-testid={`cal-day-${cell.dateStr}`}
                data-has-events={hasEvents ? "true" : "false"}
              >
                <span className={`text-[11px] sm:text-sm font-bold ${isSelected ? "text-white" : isToday ? "text-terracotta-dark" : "text-forest"}`}>{cell.day}</span>
                {hasEvents && (
                  <div className="flex-1 mt-0.5 space-y-0.5 overflow-hidden">
                    <span className="hidden sm:block truncate text-[10px] leading-tight px-1 py-0.5 rounded bg-terracotta text-white font-semibold" title={cell.events[0].title}>
                      {cell.events[0].title}
                    </span>
                    {cell.events.length > 1 && (
                      <span className="hidden sm:block text-[9px] text-forest/60 font-semibold px-1">
                        +{cell.events.length - 1} more
                      </span>
                    )}
                    <div className="sm:hidden flex items-center justify-center gap-0.5 mt-1">
                      {cell.events.slice(0, 3).map((_, i) => (
                        <span key={i} className={`w-1.5 h-1.5 rounded-full ${isSelected ? "bg-white" : "bg-terracotta"}`} />
                      ))}
                    </div>
                  </div>
                )}
              </button>
            );
          })}
        </div>
        <div className="flex flex-wrap items-center gap-3 mt-4 text-[10px] text-forest/50">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-terracotta" /> has meet-ups</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-terracotta-light border border-terracotta/40" /> today</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-sage" /> selected</span>
        </div>
      </div>

      {selectedDay && (
        <div className="mt-6" data-testid="cal-selected-day-panel">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-base sm:text-lg font-black text-forest font-heading">
              Meet-ups on {new Date(selectedDay + "T12:00:00").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
            </h4>
            <button onClick={() => setSelectedDay(null)} className="text-xs font-bold text-terracotta-dark hover:underline" data-testid="cal-clear-day-btn">
              Close
            </button>
          </div>
          {selectedEvents.length === 0 ? (
            <div className="bg-white/60 border border-sage/20 rounded-2xl p-6 text-center text-sm text-forest/60" data-testid="cal-no-events-day">
              No meet-ups on this day. Pick another date or switch back to list view.
            </div>
          ) : (
            <div className="space-y-2" data-testid="cal-day-events-list">
              {selectedEvents.map((m) => (
                <Link
                  key={m.event_id}
                  to={`/meetups?event_id=${m.event_id}`}
                  className="block bg-white/80 border border-sage/20 rounded-2xl p-4 hover:shadow-md transition-shadow"
                  data-testid={`cal-day-event-${m.event_id}`}
                >
                  <div className="flex flex-wrap items-center gap-2">
                    {m.age_range && (
                      <span className="text-[10px] font-bold uppercase tracking-wide bg-amber-100 text-amber-800 px-2.5 py-1 rounded-full">{m.age_range}</span>
                    )}
                    {m.start_time && <span className="text-xs font-bold text-forest/70">{m.start_time}</span>}
                  </div>
                  <h5 className="font-heading font-bold text-forest mt-1.5">{m.title}</h5>
                  {m.location && (
                    <p className="text-xs text-forest/60 mt-0.5 flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> {m.location}
                    </p>
                  )}
                  <div className="mt-2">
                    <AddToCalendarButton event={m} testIdSuffix={`cal-${m.event_id}`} />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Meetups() {
  const { user } = useAuth();
  const [meetups, setMeetups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_MEETUP);
  const [rsvpFor, setRsvpFor] = useState(null);
  const [rsvp, setRsvp] = useState({ children_count: 0, adults_count: 0, children: [], note: "" });
  const [agreeSafety, setAgreeSafety] = useState(false);
  const [showChildDetails, setShowChildDetails] = useState(false);
  const [chatFor, setChatFor] = useState(null);
  const [messages, setMessages] = useState([]);
  const { loc: myLoc, setLoc: setMyLoc, radius, setRadius, locating, denied: locDenied, locateMe, setManual } = useSharedLocation();
  const [locText, setLocText] = useState("");
  const [nameSearch, setNameSearch] = useState("");
  // When a notification routes here with ?event_id=XYZ, show only that card so
  // the user lands directly on the context the notification was about. Any
  // navigation that drops the query param restores the full list.
  const location = useLocation();
  const navigate = useNavigate();
  const focusEventId = new URLSearchParams(location.search).get("event_id");
  const [view, setView] = useState("list");
  const [calMonth, setCalMonth] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });
  const [selectedDay, setSelectedDay] = useState(null);
  const chatEndRef = useRef(null);
  const photoInputRef = useRef(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  // Host-meet-up age range as two selectors (0-18). We keep the existing
  // single `form.age_range` string in sync so the backend contract and card
  // badge rendering stay unchanged.
  const [ageMin, setAgeMin] = useState("");
  const [ageMax, setAgeMax] = useState("");
  const AGE_OPTIONS = Array.from({ length: 19 }, (_, i) => i); // 0..18
  const syncAgeRange = (minA, maxA) => {
    if (minA === "" && maxA === "") { setForm((f) => ({ ...f, age_range: "" })); return; }
    if (minA !== "" && maxA !== "") { setForm((f) => ({ ...f, age_range: `Ages ${minA}\u2013${maxA}` })); return; }
    if (minA !== "") { setForm((f) => ({ ...f, age_range: `Ages ${minA}+` })); return; }
    setForm((f) => ({ ...f, age_range: `Up to age ${maxA}` }));
  };

  const loadReq = useRef(0);
  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (myLoc) {
      params.set("lat", myLoc.lat);
      params.set("lng", myLoc.lng);
      params.set("radius_km", radius);
    }
    const req = ++loadReq.current;
    api.get(`/meetups?${params.toString()}`)
      .then((r) => { if (req === loadReq.current) setMeetups(r.data); })
      .catch(() => {})
      .finally(() => { if (req === loadReq.current) setLoading(false); });
  }, [myLoc, radius]);
  useEffect(load, [load]);

  useEffect(() => {
    if (!chatFor) return;
    let active = true;
    const fetchChat = () =>
      api.get(`/meetups/${chatFor}/chat`)
        .then((r) => { if (active) setMessages(r.data); })
        .catch(() => {});
    fetchChat();
    // Clear the unread meet-up-chat notification for this meet-up as soon
    // as the user opens (or is polling) the chat panel.
    api.post(`/meetups/${chatFor}/chat/read`).catch(() => {});
    const t = setInterval(() => {
      fetchChat();
      api.post(`/meetups/${chatFor}/chat/read`).catch(() => {});
    }, 8000);
    return () => { active = false; clearInterval(t); };
  }, [chatFor]);

  const isParent = user?.role === "parent";
  const canHost = user && (isParent || user.role === "admin");

  const useMyLocation = () => locateMe();
  const setManualLocation = () => setManual(locText);

  const hostMeetup = async () => {
    const capacity = form.capacity === null ? null : parseInt(form.capacity, 10);
    if (capacity !== null && !(capacity >= 1)) {
      toast.error("Enter the max number of children, or tick No limit");
      return;
    }
    try {
      await api.post("/events", { ...form, capacity, kind: "meetup", category: "Meet-up" });
      toast.success("Meet-up posted!");
      setCreateOpen(false);
      setForm(EMPTY_MEETUP);
      load();
    } catch (e) {
      toast.error(errMsg(e, "Could not post meet-up"));
    }
  };

  const uploadMeetupPhoto = async (file) => {
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      toast.error("Please pick an image file");
      return;
    }
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/meetups/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, image_url: data.path }));
      toast.success("Photo uploaded");
    } catch (e) {
      toast.error(errMsg(e, "Could not upload photo"));
    } finally {
      setUploadingPhoto(false);
      if (photoInputRef.current) photoInputRef.current.value = "";
    }
  };

  const submitRsvp = async () => {
    try {
      await api.post(`/events/${rsvpFor.event_id}/rsvp`, rsvp);
      toast.success("You're in! See you there.");
      setRsvpFor(null);
      setRsvp({ children_count: 0, adults_count: 0, children: [], note: "" });
      setAgreeSafety(false);
      setShowChildDetails(false);
      load();
    } catch (e) {
      toast.error(errMsg(e, "Could not join"));
    }
  };

  const cancelRsvp = async (eventId) => {
    try {
      await api.delete(`/events/${eventId}/rsvp`);
      toast.success("RSVP cancelled");
      load();
    } catch (e) {
      toast.error(errMsg(e, "Could not cancel"));
    }
  };

  const deleteMeetup = async (eventId) => {
    try {
      await api.delete(`/events/${eventId}`);
      toast.success("Meet-up removed");
      load();
    } catch {
      toast.error("Could not remove meet-up");
    }
  };

  const sendChat = async (eventId, textOverride) => {
    const text = (textOverride ?? "").trim();
    if (!text) return false;
    try {
      await api.post(`/meetups/${eventId}/chat`, { text });
      const { data } = await api.get(`/meetups/${eventId}/chat`);
      setMessages(data);
      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
      return true;
    } catch (e) {
      toast.error(errMsg(e, "Could not send message"));
      return false;
    }
  };

  const setChild = (i, key, val) => {
    const arr = [...rsvp.children];
    arr[i] = { ...arr[i], [key]: val };
    setRsvp({ ...rsvp, children: arr });
  };

  const today = new Date().toISOString().slice(0, 10);
  // Dynamic text filter: as the user types in the location input, narrow the
  // visible meet-ups to cards whose `location` (city/Google-location string)
  // contains the typed text. Runs on top of the existing radius filter.
  const q = locText.trim().toLowerCase();
  // Name/type/theme search tokens — every token must appear somewhere in the
  // card haystack (title, description, location, date, kind, host, type/theme
  // if present). Partial-word matches work because we use substring includes.
  const nameTokens = nameSearch.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const buildHaystack = (m) => [
    m.title, m.description, m.location, m.kind, m.type, m.theme,
    m.host_name, m.host, m.category,
    m.date,
    m.date ? new Date(m.date + "T12:00:00").toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric" }) : "",
    m.date ? new Date(m.date + "T12:00:00").toLocaleDateString(undefined, { month: "short", year: "numeric" }) : "",
  ].filter(Boolean).join(" ").toLowerCase();
  const upcoming = meetups
    .filter((m) => m.date >= today)
    .filter((m) => !q || (m.location || "").toLowerCase().includes(q))
    .filter((m) => {
      if (nameTokens.length === 0) return true;
      const hay = buildHaystack(m);
      return nameTokens.every((t) => hay.includes(t));
    });

  // When a meet-up is focused (via ?event_id=XYZ), find it from the full
  // meet-ups list (ignoring filters and the "upcoming" cutoff so past or
  // filtered-out meet-ups can still open their detail view inside the tab).
  const detailMeetup = focusEventId ? meetups.find((m) => m.event_id === focusEventId) : null;
  // Auto-open the chat panel when the detail view opens so users land on
  // the complete context in one view.
  useEffect(() => {
    if (detailMeetup) setChatFor(detailMeetup.event_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusEventId, detailMeetup?.event_id]);

  const openDetail = (eventId) => {
    navigate(`/meetups?event_id=${eventId}`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const closeDetail = () => {
    setChatFor(null);
    navigate("/meetups");
  };


  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="meetups-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <p className="text-sm text-stone-600 font-semibold tracking-wide uppercase">Find your people</p>
          <h1 className="text-3xl sm:text-4xl font-black text-forest mt-1">Parent meet-ups</h1>
          <p className="text-forest/60 mt-1">Casual hangouts hosted by local families — Spur, the ice rink, the park. All kids welcome, homeschooled or not.</p>
        </div>
        {canHost && (
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full bg-terracotta hover:bg-terracotta-dark text-white shadow-md" data-testid="host-meetup-btn">
                <Plus className="w-4 h-4 mr-2" /> Host a meet-up
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-3xl bg-cream max-h-[90vh] overflow-y-auto" data-testid="host-meetup-dialog">
              <DialogHeader>
                <DialogTitle className="font-heading text-forest">Host a meet-up</DialogTitle>
                <DialogDescription className="sr-only">Pick a venue, day, and time for other families to join.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="text-forest/80">Venue / meet-up name *</Label>
                  <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Spur Ruimsig" className="mt-1 rounded-xl bg-white border-sage/30" data-testid="meetup-title-input" />
                </div>
                <div>
                  <Label className="text-forest/80">Location *</Label>
                  <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="e.g. Cape Town Ice Rink, GrandWest" className="mt-1 rounded-xl bg-white border-sage/30" data-testid="meetup-location-input" />
                  <p className="text-[10px] text-forest/40 mt-1">Used to show distance to other families. Location data © OpenStreetMap contributors.</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-forest/80">Day *</Label>
                    <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1 rounded-xl bg-white border-sage/30" data-testid="meetup-date-input" />
                  </div>
                  <div>
                    <Label className="text-forest/80">Time</Label>
                    <Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="mt-1 rounded-xl bg-white border-sage/30" data-testid="meetup-time-input" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-forest/80">Kids' ages</Label>
                    <div className="mt-1 grid grid-cols-2 gap-2" data-testid="meetup-age-group">
                      <select
                        value={ageMin}
                        onChange={(e) => { const v = e.target.value; setAgeMin(v); syncAgeRange(v, ageMax); }}
                        className="rounded-xl bg-white border border-sage/30 px-3 py-2 text-sm text-forest focus:outline-none focus:ring-2 focus:ring-sage/40"
                        data-testid="meetup-age-min-select"
                        aria-label="Minimum age"
                      >
                        <option value="">Min</option>
                        {AGE_OPTIONS.map((n) => (
                          <option key={n} value={n} disabled={ageMax !== "" && n > Number(ageMax)}>{n}</option>
                        ))}
                      </select>
                      <select
                        value={ageMax}
                        onChange={(e) => { const v = e.target.value; setAgeMax(v); syncAgeRange(ageMin, v); }}
                        className="rounded-xl bg-white border border-sage/30 px-3 py-2 text-sm text-forest focus:outline-none focus:ring-2 focus:ring-sage/40"
                        data-testid="meetup-age-max-select"
                        aria-label="Maximum age"
                      >
                        <option value="">Max</option>
                        {AGE_OPTIONS.map((n) => (
                          <option key={n} value={n} disabled={ageMin !== "" && n < Number(ageMin)}>{n}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="meetup-capacity-input" className="text-forest/80">Max children</Label>
                    <Input id="meetup-capacity-input" type="text" inputMode="numeric" pattern="[0-9]*" autoComplete="off"
                      placeholder={form.capacity === null ? "No limit" : "e.g. 15"} disabled={form.capacity === null}
                      value={form.capacity ?? ""} onChange={(e) => setForm({ ...form, capacity: e.target.value.replace(/\D/g, "").slice(0, 4) })}
                      className="mt-1 rounded-xl bg-white border-sage/30" data-testid="meetup-capacity-input" />
                    <label className="mt-2 flex items-center gap-2 text-xs font-semibold text-forest/70 cursor-pointer">
                      <input type="checkbox" checked={form.capacity === null} onChange={(e) => setForm({ ...form, capacity: e.target.checked ? null : "" })}
                        className="w-4 h-4 accent-[#A97F50]" data-testid="meetup-capacity-no-limit" />
                      No limit
                    </label>
                  </div>
                </div>
                <div>
                  <Label className="text-forest/80">Details</Label>
                  <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} placeholder="What to expect, where to meet, what to bring..." className="mt-1 rounded-xl bg-white border-sage/30" data-testid="meetup-desc-input" />
                </div>
                <div>
                  <Label className="text-forest/80">Photo (optional)</Label>
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => uploadMeetupPhoto(e.target.files?.[0])}
                    data-testid="meetup-photo-input"
                  />
                  <div className="mt-2 flex items-center gap-3">
                    <img
                      src={form.image_url ? (/^https?:\/\//i.test(form.image_url) ? form.image_url : fileUrl(form.image_url)) : "/logo.png"}
                      alt={form.image_url ? "Meet-up photo preview" : "Default Compass Kids logo"}
                      className="w-16 h-16 rounded-2xl object-cover border border-sage/30 bg-white"
                      data-testid="meetup-photo-preview"
                    />
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="rounded-full border-sage/40"
                        onClick={() => photoInputRef.current?.click()}
                        disabled={uploadingPhoto}
                        data-testid="meetup-photo-btn"
                      >
                        <Upload className="w-3.5 h-3.5 mr-1.5" />
                        {uploadingPhoto ? "Uploading..." : (form.image_url ? "Replace" : "Upload photo")}
                      </Button>
                      {form.image_url && (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="rounded-full text-terracotta-dark hover:bg-terracotta-light"
                          onClick={() => setForm((f) => ({ ...f, image_url: "" }))}
                          data-testid="meetup-photo-clear"
                        >
                          Remove
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
                <Button onClick={hostMeetup} disabled={!form.title || !form.date || !form.location} className="w-full rounded-full bg-terracotta hover:bg-terracotta-dark text-white" data-testid="meetup-submit-btn">
                  Post meet-up
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="bg-white/80 border border-sage/20 rounded-3xl p-4 sm:p-5 mb-4 shadow-sm" data-testid="meetup-name-filter">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-forest/40" />
          <Input
            value={nameSearch}
            onChange={(e) => setNameSearch(e.target.value)}
            placeholder="Search by name, type, theme, date..."
            className="pl-9 rounded-full bg-softstone/60 border-sage/30"
            data-testid="meetup-name-search-input"
          />
        </div>
      </div>

      <div className="bg-white/80 border border-sage/20 rounded-3xl p-5 sm:p-6 mb-8 shadow-sm" data-testid="location-filter">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex-1 flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-forest/40" />
              <Input
                value={locText}
                onChange={(e) => setLocText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && setManualLocation()}
                placeholder="Your suburb or area, e.g. Roodepoort"
                className="pl-9 rounded-full bg-softstone/60 border-sage/30"
                data-testid="location-input"
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="rounded-full shrink-0 flex-1 sm:flex-none" onClick={setManualLocation} disabled={locating || locText.trim().length < 3} data-testid="set-location-btn">
                Set
              </Button>
              <Button variant="outline" className="rounded-full shrink-0 flex-1 sm:flex-none border-sage/40" onClick={useMyLocation} disabled={locating} data-testid="use-location-btn">
                <Navigation className="w-4 h-4 mr-1" /> {locating ? "Locating..." : "Use my location"}
              </Button>
            </div>
          </div>
        </div>
        {locDenied && <LocationDeniedNote testid="meetups-location-denied" />}
        {myLoc && (
          <div className="mt-4">
            <div className="flex items-center justify-between text-xs font-semibold text-forest/60 mb-2">
              <span data-testid="current-location-label">Near: {myLoc.label}</span>
              <span className="text-forest" data-testid="radius-value">Within {radius} km</span>
            </div>
            <Slider value={[radius]} onValueChange={(v) => setRadius(v[0])} min={5} max={100} step={5} className="w-full" data-testid="radius-slider" />
            <div className="flex justify-between text-[10px] text-forest/40 mt-1">
              <span>5 km</span><span>100 km</span>
            </div>
            <button onClick={() => setMyLoc(null)} className="text-xs font-bold text-terracotta-dark hover:underline mt-2" data-testid="clear-location-btn">
              Clear location filter
            </button>
          </div>
        )}
        {!myLoc && !locDenied && (
          <p className="text-xs text-forest/50 mt-3">Set your location to see meet-ups near you — the distance slider defaults to 20 km and goes up to 100 km.</p>
        )}
      </div>

      <div className="flex items-center gap-2 mb-6" data-testid="meetups-view-toggle">
        <Button
          size="sm"
          variant={view === "list" ? "default" : "outline"}
          onClick={() => setView("list")}
          className={`rounded-full ${view === "list" ? "bg-sage hover:bg-sage-dark text-white" : "border-sage/40"}`}
          data-testid="meetups-view-list-btn"
        >
          <List className="w-4 h-4 mr-1.5" /> List view
        </Button>
        <Button
          size="sm"
          variant={view === "calendar" ? "default" : "outline"}
          onClick={() => setView("calendar")}
          className={`rounded-full ${view === "calendar" ? "bg-sage hover:bg-sage-dark text-white" : "border-sage/40"}`}
          data-testid="meetups-view-calendar-btn"
        >
          <LayoutGrid className="w-4 h-4 mr-1.5" /> Calendar grid
        </Button>
      </div>

      {detailMeetup ? (
        <div data-testid="meetup-detail-view">
          <Button
            variant="ghost"
            onClick={closeDetail}
            className="mb-4 rounded-full text-forest hover:bg-sage-light"
            data-testid="meetup-detail-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to meet-ups
          </Button>
          <MeetupCard
            m={detailMeetup}
            user={user}
            isParent={isParent}
            chatFor={chatFor}
            setChatFor={setChatFor}
            messages={messages}
            chatEndRef={chatEndRef}
            cancelRsvp={cancelRsvp}
            setRsvpFor={setRsvpFor}
            setRsvp={setRsvp}
            deleteMeetup={deleteMeetup}
            sendChat={sendChat}
            detailMode
          />
        </div>
      ) : loading ? (
        <div className="text-center py-20 text-forest/50">Loading meet-ups...</div>
      ) : view === "calendar" ? (
        <CalendarGridView
          meetups={upcoming}
          calMonth={calMonth}
          setCalMonth={setCalMonth}
          selectedDay={selectedDay}
          setSelectedDay={setSelectedDay}
        />
      ) : (
        <>
          <div className="space-y-4">
            {upcoming.length === 0 && (
              <div className="bg-white/60 border border-sage/20 rounded-3xl p-12 text-center text-forest/60" data-testid="no-meetups">
                <MapPin className="w-10 h-10 text-sage mx-auto mb-3" />
                {myLoc
                  ? `No meet-ups within ${radius} km right now — try widening the radius, or host one yourself!`
                  : "No upcoming meet-ups yet. Be the first to host one — pick a spot, a day, and a time!"}
              </div>
            )}
            {upcoming.map((m) => <MeetupCard key={m.event_id} m={m} user={user} isParent={isParent} chatFor={chatFor} setChatFor={setChatFor} messages={messages} chatEndRef={chatEndRef} cancelRsvp={cancelRsvp} setRsvpFor={setRsvpFor} setRsvp={setRsvp} deleteMeetup={deleteMeetup} sendChat={sendChat} onOpenDetail={openDetail} />)}
          </div>
        </>
      )}

      <Dialog open={!!rsvpFor} onOpenChange={(o) => { if (!o) { setRsvpFor(null); setAgreeSafety(false); } }}>
        <DialogContent className="rounded-3xl bg-cream" data-testid="rsvp-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-forest">Join: {rsvpFor?.title}</DialogTitle>
            <DialogDescription className="sr-only">Register your children for this meet-up.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-forest/60">
              Joining as <strong>{user?.name}</strong> · {rsvpFor?.capacity == null ? "no limit on spots" : `${rsvpFor.capacity - rsvpFor.spots_filled} spots left`}
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-forest/80">Number of children *</Label>
                <Input type="number" min={0} max={10} value={rsvp.children_count}
                  onChange={(e) => {
                    const n = Math.max(0, Math.min(10, parseInt(e.target.value) || 0));
                    setRsvp((r) => {
                      // Grow/shrink the per-child rows to match the count.
                      const next = r.children.slice(0, n);
                      while (next.length < n) next.push({ gender: "", age: "" });
                      return { ...r, children_count: n, children: next };
                    });
                  }}
                  className="mt-1 rounded-xl bg-white border-sage/30" data-testid="rsvp-children-count" />
              </div>
              <div>
                <Label className="text-forest/80">Number of adults *</Label>
                <Input type="number" min={0} max={10} value={rsvp.adults_count}
                  onChange={(e) => setRsvp({ ...rsvp, adults_count: Math.max(0, parseInt(e.target.value) || 0) })}
                  className="mt-1 rounded-xl bg-white border-sage/30" data-testid="rsvp-adults-count" />
              </div>
            </div>
            {rsvp.children_count > 0 && (
              <div className="space-y-2" data-testid="child-details">
                <Label className="text-forest/80">Children details</Label>
                {rsvp.children.map((c, i) => (
                  <div key={i} className="grid grid-cols-[1fr_90px] gap-2" data-testid={`child-row-${i}`}>
                    <select
                      value={c.gender || ""}
                      onChange={(e) => setChild(i, "gender", e.target.value)}
                      className="rounded-xl bg-white border border-sage/30 px-3 py-2 text-sm text-forest focus:outline-none focus:ring-2 focus:ring-sage/40"
                      data-testid={`child-gender-${i}`}
                      aria-label={`Child ${i + 1} gender`}
                    >
                      <option value="">Gender</option>
                      <option value="female">Female</option>
                      <option value="male">Male</option>
                      <option value="other">Other</option>
                    </select>
                    <Input
                      type="number"
                      min={0}
                      max={18}
                      value={c.age}
                      onChange={(e) => setChild(i, "age", e.target.value)}
                      placeholder="Age"
                      className="rounded-xl bg-white border-sage/30"
                      data-testid={`child-age-${i}`}
                    />
                  </div>
                ))}
              </div>
            )}
            <div>
              <Label className="text-forest/80">Note for the host</Label>
              <Textarea value={rsvp.note} onChange={(e) => setRsvp({ ...rsvp, note: e.target.value })} rows={2}
                placeholder="Anything the host should know..." className="mt-1 rounded-xl bg-white border-sage/30" data-testid="rsvp-note-input" />
            </div>
            <div className="rounded-xl bg-softstone/50 border border-sage/20 p-3 space-y-2" data-testid="rsvp-safety-block">
              <details className="group" data-testid="rsvp-safety-details">
                <summary className="cursor-pointer text-xs font-bold text-terracotta-dark select-none">
                  Read the Compass Kids safety rules
                </summary>
                <div className="mt-3 max-h-64 overflow-y-auto pr-2 border-t border-sage/20 pt-3">
                  <SafetyRulesContent variant="compact" testIdPrefix="rsvp-safety-rules" />
                </div>
              </details>
              <label className="flex items-start gap-2 text-xs text-forest/80 cursor-pointer" data-testid="rsvp-agree-safety-label">
                <input
                  type="checkbox"
                  checked={agreeSafety}
                  onChange={(e) => setAgreeSafety(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-sage-dark cursor-pointer shrink-0"
                  data-testid="rsvp-agree-safety-checkbox"
                />
                <span>
                  I agree to follow the{" "}
                  <Link to="/safety-rules" target="_blank" rel="noopener noreferrer" className="font-bold text-terracotta-dark hover:underline" data-testid="rsvp-safety-rules-link">
                    Compass Kids Safety Rules
                  </Link>
                  {" "}as a helpful guide for safe meetups, using good judgment and remaining responsible for supervising my own children.
                </span>
              </label>
            </div>
            <Button onClick={submitRsvp} disabled={!agreeSafety} className="w-full rounded-full bg-terracotta hover:bg-terracotta-dark text-white" data-testid="rsvp-submit-btn">
              Confirm & join
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
